import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import { clearTokens, setTokens } from "../../auth/tokenManager";
import useSignOutOnEntry from "../../auth/useSignOutOnEntry";
import PhoneInput from "../common/phoneInput/phoneInput";
import translations from "../../translations/translations";
import companyBasedRequest from "../../request/companyBasedRequest";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import PasswordField from "../common/passwordField/passwordField";
import PasswordStrength from "../common/passwordStrength/passwordStrength";
import { Check, Mail, X } from "lucide-react";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import RegisterStaffSkeleton from "./registerStaffSkeleton/registerStaffSkeleton";
import logo from "../../images/LogoFullText.png";
import "./registerStaff.css";

const RegisterStaff = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const navigate = useNavigate();

	const [rememberMe, setRememberMe] = useState(false);
	const [verifying, setVerifying] = useState(true);
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [shopId, setShopId] = useState("");
	const [inviteId, setInviteId] = useState("");
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [activeModal, setActiveModal] = useState(null);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
    const [blockingError, setBlockingError] = useState(false);
	const [invalidFields, setInvalidFields] = useState(new Set());

	const { setUserData, language } = useContext(CombinedContext);

	/* Arriving here signs out whoever was already logged in — see the hook. */
	useSignOutOnEntry();

	const showBanner = (type, text, duration = 2500) => {
		setBanner({ type, text });
		setExiting(false);
		setTimeout(() => setExiting(true), duration - 250);
		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	const showPersistentBanner = (type, text) => {
		setBanner({ type, text });
		setExiting(false);
        if (type === "error") setBlockingError(true);
	};

	useEffect(() => {
		const verifyInvite = async () => {
			try {
				const res = await companyBasedRequest.verifyInvite(token);
				setInviteId(res.inviteId);
				setShopId(res.shopId);
				setFirstName(res.firstName);
				setLastName(res.lastName);
				setEmail(res.email || "");
			} catch (err) {
				const code = err.response?.data?.error;

				const messages = {
					staff_exists: {
						es: "Ya existe una cuenta de personal activa con este correo. Inicia sesión o usa un correo diferente.",
						en: "There's already an active staff account with this email. Sign in or use a different email.",
					},
					customer_exists: {
						es: "Ya tienes una cuenta de cliente activa con este correo. Desactívala o solicita tu invitación de personal con un correo diferente.",
						en: "You already have an active customer account with this email. Deactivate it or request your staff invite with a different email.",
					},
				};

				const msg =
					messages[code]?.[language] ??
					(language === "es" ? "Este enlace de invitación ya fue usado o expiró. Solicita uno nuevo." : "This invite link has already been used or has expired. Please request a new one.");

				showPersistentBanner("error", msg);
			} finally {
				setVerifying(false);
			}
		};

		verifyInvite();
	}, [token]);

	if (verifying) return <RegisterStaffSkeleton />;

	const normalizePhoneNumber = (raw) => {
		if (!raw) return "";
		let cleaned = raw.trim().replace(/[^0-9+]/g, "");
		if (cleaned.startsWith("++")) cleaned = cleaned.replace(/^\++/, "+");
		if (!cleaned.startsWith("+")) cleaned = "+1" + cleaned;
		cleaned = cleaned.replace(/(?!^)\+/g, "");
		if (!/^\+\d{10,15}$/.test(cleaned)) return "";
		return cleaned;
	};

	function isStrongPassword(pwd) {
		return (
			typeof pwd === "string" &&
			pwd.length >= 8 &&
			/[A-Z]/.test(pwd) &&
			/[a-z]/.test(pwd) &&
			/[0-9]/.test(pwd) &&
			/[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(pwd)
		);
	}

	/* The banner says what's wrong; these say where. A flagged field clears
	   as soon as it's touched, so the red only ever marks what's still open. */
	const clearInvalid = (field) => {
		setInvalidFields((prev) => {
			if (!prev.has(field)) return prev;
			const next = new Set(prev);
			next.delete(field);
			return next;
		});
	};

	const invalidClass = (field) => (invalidFields.has(field) ? " is-invalid" : "");

	const handleRegisterAdmin = async () => {
		const missing = new Set();
		if (!firstName) missing.add("firstName");
		if (!lastName) missing.add("lastName");
		if (!phoneNumber) missing.add("phone");
		if (!password) missing.add("password");
		if (!confirmPassword) missing.add("confirmPassword");

		if (missing.size > 0) {
			setInvalidFields(missing);
			showBanner("error", language === "es" ? "¡Casi listo! Completa los campos obligatorios para continuar." : "Almost there! Please fill in the required fields to continue.");
			return;
		}

		if (!acceptedTerms) {
			setInvalidFields(new Set(["terms"]));
			showBanner("error", translations[language].registerStaff.errors.mustAcceptTerms);
			return;
		}

		if (!isStrongPassword(password)) {
			setInvalidFields(new Set(["password"]));
			showBanner(
				"error",
				language === "es"
					? "Tu contraseña necesita al menos 8 caracteres, con una mayúscula, una minúscula, un número y un carácter especial."
					: "Your password needs at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character.",
			);
			return;
		}

		if (password !== confirmPassword) {
			setInvalidFields(new Set(["confirmPassword"]));
			showBanner("error", language === "es" ? "Las contraseñas no coinciden. Revísalas e inténtalo de nuevo." : "Those passwords don't match — give it another look.");
			return;
		}

		setInvalidFields(new Set());
		setLoading(true);

		try {
			const payload = {
				token,
				shopId,
				inviteId,
				email,
				password,
				firstName,
				lastName,
				phone: normalizePhoneNumber(phoneNumber),
				acceptedTermsVersion: "1.0",
			};

			const res = await companyBasedRequest.registerStaff(payload);

			const formattedData = {
				...res,
				cognitoTokens: {
					IdToken: res.cognitoTokens?.IdToken || res.IdToken,
					AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
					RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
				},
			};

			setUserData(formattedData);

			const storage = rememberMe ? localStorage : sessionStorage;
			// Centralised so a leftover token in the other storage cannot
			// shadow this brand-new session.
			clearTokens();
			setTokens(
				{
					idToken: formattedData.cognitoTokens?.IdToken,
					accessToken: formattedData.cognitoTokens?.AccessToken,
					refreshToken: formattedData.cognitoTokens?.RefreshToken,
				},
				{ storage }
			);
			storage.setItem("user", JSON.stringify(formattedData.user));

			navigate("/calendar");
		} catch (err) {
			console.error("Register staff failed:", err);

			const code = err.response?.data?.error;

			const errorMessages = {
				email_in_use: {
					es: "Ya existe una cuenta con este correo electrónico. Inicia sesión o usa un correo diferente.",
					en: "An account with this email already exists. Please sign in or use a different email.",
				},
				invalid_password: {
					es: "Tu contraseña no cumple los requisitos mínimos (mayúscula, minúscula y un número, mínimo 8 caracteres).",
					en: "Your password doesn't meet the minimum requirements (uppercase, lowercase, and a number, at least 8 characters).",
				},
				invalid_parameter: {
					es: "Revisa tu correo y número de teléfono — alguno de los dos no tiene un formato válido.",
					en: "Please check your email and phone number — one of them isn't in a valid format.",
				},
				too_many_requests: {
					es: "Demasiados intentos. Espera un momento e inténtalo de nuevo.",
					en: "Too many attempts. Please wait a moment and try again.",
				},
			};

			const msg =
				errorMessages[code]?.[language] ??
				err.message ??
				(language === "es" ? "No pudimos registrar tu cuenta. Inténtalo de nuevo." : "We couldn't register your account. Please try again.");

			showPersistentBanner("error", msg);
		} finally {
			setLoading(false);
		}
	};

	const t = translations[language].registerStaff.step1;

	return (
		<div className="sf-auth-page is-wide sf-rst-root">
			<div className="sf-auth-shell">
				<div className="sf-auth-card">
					<div className="sf-auth-head">
						<h1 className="sf-auth-title">{t.title}</h1>
						<p className="sf-auth-sub">{t.subtitle}</p>
					</div>

					{/* The invite is tied to this address — surface it instead of
					    hiding it in a greyed-out field they'll try to edit. */}
					{email && (
						<div className="sf-auth-callout">
							<span className="sf-auth-callout-icon">
								<Mail size={18} strokeWidth={2} />
							</span>
							<span className="sf-auth-callout-copy">
								<em>{t.invitedTo}</em>
								<strong>{email}</strong>
							</span>
						</div>
					)}

					<p className="sf-auth-legend">{t.sectionAbout}</p>

					{/* Name row */}
					<div className="sf-auth-row2">
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.firstName}</label>
							<input
								className={`sf-auth-input${invalidClass("firstName")}`}
								placeholder={t.firstName}
								value={firstName}
								onChange={(e) => {
									setFirstName(e.target.value);
									clearInvalid("firstName");
								}}
								autoComplete="given-name"
							/>
						</div>
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.lastName}</label>
							<input
								className={`sf-auth-input${invalidClass("lastName")}`}
								placeholder={t.lastName}
								value={lastName}
								onChange={(e) => {
									setLastName(e.target.value);
									clearInvalid("lastName");
								}}
								autoComplete="family-name"
							/>
						</div>
					</div>

					{/* Phone */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.phone}</label>
						<PhoneInput
							businessPhone={phoneNumber}
							setBusinessPhone={(value) => {
								setPhoneNumber(value);
								clearInvalid("phone");
							}}
							className={invalidFields.has("phone") ? "is-invalid" : ""}
						/>
					</div>

					<p className="sf-auth-legend">{t.sectionSecurity}</p>

					{/* Password */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.password}</label>
						<PasswordField
							value={password}
							onChange={(value) => {
								setPassword(value);
								clearInvalid("password");
							}}
							placeholder={t.password}
							autoComplete="new-password"
							className={invalidClass("password").trim()}
						/>
						<PasswordStrength value={password} />
					</div>

					{/* Confirm password */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.confirmPassword}</label>
						<PasswordField
							value={confirmPassword}
							onChange={(value) => {
								setConfirmPassword(value);
								clearInvalid("confirmPassword");
							}}
							placeholder={t.confirmPassword}
							autoComplete="new-password"
							className={invalidClass("confirmPassword").trim()}
						/>
						{confirmPassword && (
							<p className={`sf-auth-match ${password === confirmPassword ? "is-ok" : "is-bad"}`} aria-live="polite">
								{password === confirmPassword ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
								{password === confirmPassword
									? translations[language].register.match.ok
									: translations[language].register.match.bad}
							</p>
						)}
					</div>

					{/* Remember me */}
					<div className="sf-auth-check is-quiet">
						<input type="checkbox" id="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
						<label htmlFor="rememberMe">{t.remember}</label>
					</div>

					{/* Terms */}
					<div className={`sf-auth-check${invalidClass("terms")}`}>
						<input
							type="checkbox"
							id="acceptTerms"
							checked={acceptedTerms}
							onChange={(e) => {
								setAcceptedTerms(e.target.checked);
								clearInvalid("terms");
							}}
						/>
						<label htmlFor="acceptTerms">
							{translations[language].register.acceptTerms}{" "}
							<span className="sf-auth-check-link" onClick={() => setActiveModal("terms")}>
								{translations[language].register.termsLink}
							</span>
						</label>
					</div>

					{/* Submit */}
					<button className="sf-auth-cta" onClick={handleRegisterAdmin} disabled={loading || !password || !confirmPassword || blockingError}>
						{loading && <span className="sf-auth-spinner" aria-hidden="true" />}
						{loading ? t.registering : t.register}
					</button>
				</div>
			</div>

			{banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}
			{activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
		</div>
	);
};

export default RegisterStaff;
